import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { sizeChunckOfGroupsNewAuthorSearchFilter } from '@Containers/ResultsFilters/ResultsFiltersComponents/BarChartCheckboxesDialog/BarChartCheckboxesDialogConstants';
import { byValue, occurrence, ascending, descending } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import CustomGroupsBar from '@Containers/ResultsFilters/ResultsFiltersComponents/BarChartCheckboxesDialog/CustomGroupsBar';
import TopPanel from '@Containers/ResultsFilters/ResultsFiltersComponents/NewAuthorCheckboxesDialog/Components/TopPanel';
import BannerAffiliationLink from '@Containers/ResultsFilters/ResultsFiltersComponents/NewAuthorCheckboxesDialog/Components/BannerAffiliationLink';
import ActiveChunk from '@Containers/ResultsFilters/ResultsFiltersComponents/NewAuthorCheckboxesDialog/Components/ActiveChunk';
import ActiveChunkOptimizedRendering from '@Containers/ResultsFilters/ResultsFiltersComponents/NewAuthorCheckboxesDialog/Components/ActiveChunkOptimizedRendering';

const spinnerId = 'ActiveChunkSpinner';
export const SHOW_BANNER_GROUP_COUNT = 2000;

function DialogContent({
  selfClose: dialogClose,
  id: dialogId,
  filter,
  activeChunk,
  onCheckboxClick,
  selectedCount,
  onClickClearSelected,
  disableClearSelected,
  onDescending,
  disableDescending,
  onAscending,
  disableAscending,
  sortingType,
  onOccurrence,
  onByValue,
  customGroupsWithByValueSorting,
  customGroupsData,
  activeCustomGroupIndex,
  setActiveCustomGroupIndex,
  sorting,
  isAuthorsOfSciDocFilter,
  openAffiliationFilter,
}) {
  const onBannerAffiliationLinkClick = useCallback(() => {
    if (dialogClose) {
      dialogClose();
    }
    openAffiliationFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!activeChunk) {
    return <RxSpinnerComponent autoStart spinnerId={spinnerId} />;
  }

  const shouldUseActiveChunkOptimizedRendering = isAuthorsOfSciDocFilter && activeChunk.count() > sizeChunckOfGroupsNewAuthorSearchFilter;
  const shouldShowBannerAffiliationLink = isAuthorsOfSciDocFilter && filter.get('groupCount') > SHOW_BANNER_GROUP_COUNT;

  return (
    <>
      <TopPanel
        selectedCount={selectedCount}
        onClickClearSelected={onClickClearSelected}
        disableClearSelected={disableClearSelected}
        onDescending={onDescending}
        disableDescending={disableDescending}
        onAscending={onAscending}
        disableAscending={disableAscending}
        sortingType={sortingType}
        onOccurrence={onOccurrence}
        onByValue={onByValue}
      />
      {customGroupsWithByValueSorting && (
        <CustomGroupsBar
          customGroupsData={customGroupsData}
          activeCustomGroupIndex={activeCustomGroupIndex}
          setActiveCustomGroupIndex={setActiveCustomGroupIndex}
          dialogId={dialogId}
          sorting={sorting}
          onAscending={onAscending}
        />
      )}
      {shouldShowBannerAffiliationLink && <BannerAffiliationLink onLinkClick={onBannerAffiliationLinkClick} />}
      {shouldUseActiveChunkOptimizedRendering ? (
        <ActiveChunkOptimizedRendering
          activeChunk={activeChunk}
          onCheckboxClick={onCheckboxClick}
          isAuthorsOfSciDocFilter={isAuthorsOfSciDocFilter}
        />
      ) : (
        <ActiveChunk activeChunk={activeChunk} onCheckboxClick={onCheckboxClick} isAuthorsOfSciDocFilter={isAuthorsOfSciDocFilter} />
      )}
    </>
  );
}

DialogContent.propTypes = {
  selfClose: PropTypes.func,
  id: PropTypes.string,
  filter: PropTypes.instanceOf(Map),
  activeChunk: PropTypes.instanceOf(List),
  onCheckboxClick: PropTypes.func.isRequired,
  selectedCount: PropTypes.number,
  onClickClearSelected: PropTypes.func.isRequired,
  disableClearSelected: PropTypes.bool,
  onDescending: PropTypes.func.isRequired,
  disableDescending: PropTypes.bool,
  onAscending: PropTypes.func.isRequired,
  disableAscending: PropTypes.bool,
  sortingType: PropTypes.oneOf([occurrence, byValue]),
  onOccurrence: PropTypes.func.isRequired,
  onByValue: PropTypes.func.isRequired,
  customGroupsWithByValueSorting: PropTypes.bool,
  customGroupsData: PropTypes.array,
  activeCustomGroupIndex: PropTypes.string,
  setActiveCustomGroupIndex: PropTypes.func,
  sorting: PropTypes.oneOf([ascending, descending]),
  isAuthorsOfSciDocFilter: PropTypes.bool,
  openAffiliationFilter: PropTypes.func,
};

export default DialogContent;
